import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { Clock, MoreHorizontal, Trash2, Users } from "lucide-react";
import { mutate } from "swr";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { type ViewMode } from "~/hooks/useViewMode";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import {
  ProjectListItem,
  ProjectShare,
  ShareLevel,
  SharePrincipalType,
  deleteProject,
  projectListPath,
} from "~/services/projects";
import { DeleteProjectDialog } from "./DeleteProjectDialog";
import { ProjectAccessBadge } from "./ProjectAccessBadge";

interface ProjectCardProps {
  project: ProjectListItem;
  // Collaborator shares, loaded in a second phase. `undefined` while in flight.
  shares?: ProjectShare[];
  view?: ViewMode;
}

const MAX_TEAM_AVATARS = 3;
const MAX_PEOPLE_AVATARS = 3;

const shareName = (share: ProjectShare): string => {
  if (share.principal_type === SharePrincipalType.USER_TAG) {
    return share.display_name || share.principal_id;
  }
  return [share.given_name, share.family_name].filter(Boolean).join(" ") || share.principal_id;
};

const AgentAvatars = ({ project, ringClass }: { project: ProjectListItem; ringClass: string }) => {
  const agents = project.agents;
  if (agents.length === 0) return null;
  const overflow = agents.length - MAX_TEAM_AVATARS;
  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center">
        {agents.slice(0, MAX_TEAM_AVATARS).map((agent, i) => (
          <Tooltip key={agent.id}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  `relative inline-flex ring-2 ${ringClass} rounded-full transition-transform duration-75 hover:z-10 hover:scale-110`,
                  i > 0 && "-ml-1"
                )}
              >
                <AgentAvatar id={agent.id} name={agent.name} size={24} />
              </span>
            </TooltipTrigger>
            <TooltipContent>{agent.display_name}</TooltipContent>
          </Tooltip>
        ))}
      </div>
      {overflow > 0 && <span className="text-xs text-muted-foreground">+{overflow}</span>}
    </div>
  );
};

const PeopleAvatars = ({
  shares,
  ringClass,
}: {
  shares: ProjectShare[] | undefined;
  ringClass: string;
}) => {
  if (shares === undefined) {
    // Shares resolve after the card; hold the slot with a skeleton avatar.
    return (
      <span
        aria-hidden="true"
        className={cn("inline-flex size-6 animate-pulse rounded-full bg-muted ring-2", ringClass)}
      />
    );
  }
  if (shares.length === 0) return null;
  const overflow = shares.length - MAX_PEOPLE_AVATARS;
  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center">
        {shares.slice(0, MAX_PEOPLE_AVATARS).map((share, i) => {
          const isGroup = share.principal_type === SharePrincipalType.USER_TAG;
          return (
            <Tooltip key={`${share.principal_type}:${share.principal_id}`}>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    `relative inline-flex ring-2 ${ringClass} rounded-full transition-transform duration-75 hover:z-10 hover:scale-110`,
                    i > 0 && "-ml-1"
                  )}
                >
                  {isGroup ? (
                    <span className="flex size-6 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Users className="size-3.5" aria-hidden="true" />
                    </span>
                  ) : (
                    <Avatar className="size-6">
                      <AvatarImage src={share.image_url ?? undefined} />
                      <AvatarFallback>{shareName(share).charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent>{shareName(share)}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      {overflow > 0 && <span className="text-xs text-muted-foreground">+{overflow}</span>}
    </div>
  );
};

export const ProjectCard = ({ project, shares, view = "card" }: ProjectCardProps) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteProject(project.short_id);
      await mutate(projectListPath());
      showSuccess(`"${project.name}" deleted`);
    } catch {
      showError("Failed to delete project");
    }
  };

  const updatedAt = formatDistanceToNowStrict(new Date(project.updated_at), { addSuffix: true });

  const isOwner = project.access_level === ShareLevel.OWNER;

  // Each action is gated by its own permission; the menu renders only when at
  // least one action survives, so the trigger can never open onto an empty menu.
  const menuItems = [
    isOwner && (
      <DropdownMenuItem key="delete" variant="destructive" onClick={() => setDeleteOpen(true)}>
        <Trash2 />
        Delete
      </DropdownMenuItem>
    ),
  ].filter(Boolean);

  const dropdown = menuItems.length > 0 && (
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "size-7 shrink-0 text-muted-foreground",
              "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100",
              "focus-visible:ring-0 focus-visible:border-transparent"
            )}
            aria-label={`Actions for ${project.name}`}
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">{menuItems}</DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  if (view === "list") {
    return (
      <>
        <div className="group relative">
          <div
            className={cn(
              "flex items-center gap-4 rounded-lg bg-card px-4 py-5",
              "cursor-pointer transition-colors duration-150 group-hover:bg-muted"
            )}
            onClick={() => navigate(nextgenProjectPath(project.short_id))}
          >
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-semibold text-card-foreground">
                {project.name}
              </h3>
              <p
                className={cn(
                  "truncate text-xs text-muted-foreground",
                  !project.description && "italic text-muted-foreground/60"
                )}
              >
                {project.description || "No description"}
              </p>
            </div>
            <div className="w-20 shrink-0">
              <ProjectAccessBadge accessLevel={project.access_level} />
            </div>
            <div className="flex w-36 shrink-0 items-center gap-2">
              <PeopleAvatars shares={shares} ringClass="ring-card" />
              <AgentAvatars project={project} ringClass="ring-card" />
            </div>
            <p className="flex w-36 shrink-0 items-center justify-end gap-1 text-xs text-muted-foreground">
              <Clock className="size-3.5" aria-hidden="true" />
              {updatedAt}
            </p>
            {dropdown}
          </div>
        </div>
        <DeleteProjectDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          projectName={project.name}
          onConfirm={handleDelete}
        />
      </>
    );
  }

  return (
    <>
      <div className="group relative h-full transition-transform duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] hover:scale-[1.02]">
        <Card
          className={cn(
            "relative h-full gap-0 overflow-hidden p-0",
            "cursor-pointer shadow-[0_1px_2px_rgba(15,23,42,0.03)]",
            "border-l-[10px] border-l-[var(--card-header-bg)] rounded-tl-sm rounded-bl-sm"
          )}
          onClick={() => navigate(nextgenProjectPath(project.short_id))}
        >
          <div className="flex flex-col gap-3 px-5 pt-5 pb-4">
            <div className="flex items-start justify-between gap-2">
              <h3 className="min-w-0 flex-1 truncate text-base font-semibold leading-tight text-card-foreground group-hover:text-foreground">
                {project.name}
              </h3>
              {dropdown}
            </div>

            <p
              className={cn(
                "line-clamp-2 text-sm text-muted-foreground",
                !project.description && "italic text-muted-foreground/60"
              )}
            >
              {project.description || "No description"}
            </p>
          </div>

          <div className="flex items-center gap-3 px-5 py-2.5">
            <ProjectAccessBadge accessLevel={project.access_level} />
            <PeopleAvatars shares={shares} ringClass="ring-card" />
            <AgentAvatars project={project} ringClass="ring-card" />
            <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="size-3.5" aria-hidden="true" />
              {updatedAt}
            </span>
          </div>
        </Card>

        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 rounded-xl",
            "opacity-0 shadow-[0_4px_10px_rgba(15,23,42,0.06)]",
            "transition-opacity duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] group-hover:opacity-100"
          )}
        />
      </div>

      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        projectName={project.name}
        onConfirm={handleDelete}
      />
    </>
  );
};
